import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AccountModule } from '@/core/modules/account/account.module'
import { MdrModule } from '@/core/modules/mdr'
import type { TMdrTabs } from '@/core/modules/mdr/mdr'
import type { MenuItem } from '@/ui/components/SimpleMenu/SimpleMenu'
import { timeRangeOptions } from '@/ui/routes/mdr/routes/overview'
import { Analytics } from '@/core/services/mixpanel'
import { useMdrCasesQuery } from '@core/hooks/useMdrCasesQuery'

type UseMdrRouteReturn = {
    isMdrActive: boolean
    showDemo: boolean
    isLoading: boolean
    isLoadingMore: boolean
    isError: boolean
    activeTab: TMdrTabs
    timeRangeLabel: string
    timeRangeItems: MenuItem[]
    timeRangeMenuOpen: boolean
    onTimeRangeActivate: () => void
    onTimeRangeClose: () => void
    handleTakeALook: () => void
}

const useMdrRoute = (): UseMdrRouteReturn => {
    const [timeRangeMenuOpen, setTimeRangeMenuOpen] = useState(false)
    const { pathname } = useLocation()
    const { isLoading, isLoadingMore, isError } = useMdrCasesQuery()
    const tabKey = pathname.split('/')[2] as TMdrTabs | undefined
    const [activeTab, setActiveTab] = useState<TMdrTabs>(
        (tabKey as TMdrTabs) || 'overview'
    )
    const { isMdrActive } = AccountModule.useStore()
    const showDemo = MdrModule.getShowDemo()

    useEffect(() => {
        setActiveTab((tabKey || 'overview') as TMdrTabs)
    }, [tabKey])

    const timeRange = MdrModule.getTimeRange()

    const timeRangeItems: MenuItem[] = useMemo(
        () =>
            timeRangeOptions.map((option) => ({
                id: option.id,
                value: option.label,
                active: option.id !== timeRange.id,
                action: () => {
                    MdrModule.setTimeRange(option)
                },
            })),
        [timeRange.id]
    )

    const handleTakeALook = () => {
        Analytics.event('SEM::mdr_take_a_look_clicked')
        MdrModule.setShowDemo(true)
    }

    const onTimeRangeActivate = () => setTimeRangeMenuOpen(!timeRangeMenuOpen)
    const onTimeRangeClose = () => setTimeRangeMenuOpen(false)

    return {
        isMdrActive,
        showDemo,
        isLoading,
        isLoadingMore,
        isError,
        activeTab,
        timeRangeLabel: timeRange.label,
        timeRangeItems,
        timeRangeMenuOpen,
        onTimeRangeActivate,
        onTimeRangeClose,
        handleTakeALook,
    }
}

export default useMdrRoute
